<template>
    <div class="card flex-grow-1 limit-height">
        <div v-show="panel.header !== ''" class="card-header">
            <p class="h5 mb-0 panel-header">{{ panel.header }}</p>
        </div>
        <div
            class="card-body panel-card-body py-0 d-flex flex-column global-theme-text my-1 conversation-container"
            ref="conversationContainer"
        >
            <div class="py-1"></div>
            <chat-conversation :messages="conversationMessages" :enable-audio="true"></chat-conversation>
        </div>
        <div class="card-footer">
            <div class="d-flex align-items-center" style="margin-top: 6px; margin-bottom: 6px">
                <div class="flex-grow-1 mr-1">
                    <input
                        type="text"
                        class="form-control chat-input"
                        :placeholder="labelsTranslated.saySomethingHere + '...'"
                        v-model="inputText"
                        v-on:keyup.enter="submitChatInput()"
                        maxlength="255"
                        :disabled="locked"
                    />
                </div>
                <div class="flex-grow-0 flex-shrink-0">
                    <div
                        class="icon icon-glyphicons-basic-circle-up chat-submit-btn align-middle"
                        :class="{ 'chat-submit-btn-disabled': locked }"
                        @click="submitChatInput()"
                        role="button"
                        :disabled="locked || !inputText"
                    ></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import ChatConversation from "@/components/ChatConversation"

export default {
    name: "CustomGptPanel",
    components: {
        ChatConversation,
    },
    props: {
        node: {
            type: Object,
            required: true,
        },
        panel: {
            type: Object,
            required: true,
        },
        sessionId: {
            type: [String, Number],
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        locked: {
            type: Boolean,
            required: false,
        },
    },
    data() {
        return {
            inputText: "",
            avatarThinking: false,
            conversationMessages: [],
        }
    },
    computed: {
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        // A set of correct intent IDs discovered.
        correctIntentIdSet() {
            // Extract the list of conversation messages into a unique list of IDs representing
            // correct intents that were discovered.
            return this.conversationMessages.reduce((accumulator, message) => {
                if (message.intents_discovered) {
                    message.intents_discovered.forEach((intent) => {
                        // Add the intent ID if it's correct.
                        if (intent.correct) {
                            accumulator.add(intent.chat_agent_intent.id)
                        }
                    })
                }
                return accumulator
            }, new Set())
        },
        // The count of discovered correct intents.
        correctIntentCount() {
            return Array.from(this.correctIntentIdSet).length
        },
        // Returns true if there's only one column on the current node (used for styling).
        isSingleColumnNode() {
            return this.node.columns.length === 1
        },
    },
    watch: {
        correctIntentCount(value) {
            this.$emit("update:correctResponsesCount", value)
        },
    },
    methods: {
        // Submit the learner's text input to the GPT.
        submitChatInput() {
            if (!this.inputText) {
                return
            }
            if (this.conversationMessages[0].isInitialMessage) {
                this.conversationMessages.splice(0, 1)
            }
            // Add the learner's message to the list of messages immediately.
            this.conversationMessages.push({
                type: "learner",
                content: this.inputText,
            })
            // Make sure the most recent input is visible.
            this.scrollChatToBottomNextTick()
            let savedInputText = this.inputText
            // Clear the message input field.
            this.inputText = ""
            // Add a response message immediately in the 'thinking' state.
            let responseMessage = {
                type: "system",
                content: "",
                isThinking: true,
            }
            let vm = this
            vm.conversationMessages.push(responseMessage)
            http.post(`/api/students/sessions/${this.sessionId}/panel/${this.panel.id}/chat/`, {
                inputText: savedInputText,
            })
                .then((response) => {
                    // Add the response content to the message.
                    responseMessage.content = response.data.content
                    responseMessage.audio = response.data.audio_file
                    let intentsDiscovered = response.data.intents_discovered.map((i) => {
                        return {
                            ...i,
                            // Add a flag on each discovered intent representing if it's new or not.
                            isNewDiscovery: !vm.correctIntentIdSet.has(i.chat_agent_intent.id),
                        }
                    })
                    vm.$set(responseMessage, "intents_discovered", intentsDiscovered)
                    responseMessage.isThinking = false
                    // Scroll to the bottom of the conversation again to make the system response visible.
                    vm.scrollChatToBottomNextTick()
                    vm.$emit("response-received")
                })
                .catch(() => {
                    responseMessage.isThinking = false
                })
        },
        // Scroll to the bottom of the chat conversation on the next render.
        scrollChatToBottomNextTick() {
            this.$nextTick(() => {
                let div = this.$refs.conversationContainer
                div.scrollTop = div.scrollHeight
            })
        },
    },
    mounted() {
        if (this.conversationMessages.length) {
            // Don't initialize the state if there are already conversation messages present.
            // This fixes an issue where toggling edit mode clears the conversation history.
            return
        }
        if (this.savedResponses.length) {
            let lastConversation = this.savedResponses[this.savedResponses.length - 1]
            this.conversationMessages = lastConversation.messages
        } else {
            // Add an initial message to be displayed.
            this.conversationMessages.push({
                type: "learner",
                content: this.labelsTranslated.youConversationWillAppearHere,
                isInitialMessage: true,
            })
        }
        this.scrollChatToBottomNextTick()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/chat.scss";

.limit-height {
    max-height: 500px;
    min-height: 500px;
}

.conversation-container {
    overflow-y: scroll;
}

.card-footer {
    border-bottom-left-radius: $border-radius-lg;
    border-bottom-right-radius: $border-radius-lg;
}
</style>